import * as THREE from 'three';
import { EXTRUDED_PROFILE_SURFACE_BIAS, FRAME_EXTENT_CIRCLE_SEGMENTS } from '../../_data/frameExtent.js';
import { sectorSliceHalfHeight } from './sectorSliceSurface.js';
import { toothedBandMaxHalfHeight, toothedBandMaxRadius } from './toothedBand.js';
export function boxExtentPoints(center, extent) {
    return [-1, 1].flatMap((x) => [-1, 1].flatMap((y) => [-1, 1].map((z) => [
        center[0] + x * extent[0], center[1] + y * extent[1], center[2] + z * extent[2],
    ])));
}
/** A circumscribed prism encloses the entire circle, not only its sampled vertices. */
export function circularExtentPoints(radius, halfHeight) {
    const outer = radius / Math.cos(Math.PI / FRAME_EXTENT_CIRCLE_SEGMENTS);
    return Array.from({ length: FRAME_EXTENT_CIRCLE_SEGMENTS }, (_, index) => {
        const angle = index * Math.PI * 2 / FRAME_EXTENT_CIRCLE_SEGMENTS;
        return [-halfHeight, halfHeight].map((y) => [outer * Math.cos(angle), y, outer * Math.sin(angle)]);
    }).flat();
}
export function geometryExtentPoints(spec, shape) {
    if (shape?.shapeKind === 'extruded-profile') {
        const radius = Math.hypot(shape.innerWidth / 2 + shape.lipThickness, shape.lipHeight + shape.floorDepth + EXTRUDED_PROFILE_SURFACE_BIAS);
        return [shape.from, shape.to].flatMap((center) => boxExtentPoints(center, [radius, radius, radius]));
    }
    if (shape?.shapeKind === 'cylinder-wall') {
        const axis = new THREE.Vector3(...shape.axis);
        if (!axis.toArray().every(Number.isFinite) || axis.lengthSq() === 0) {
            throw new Error('Cylinder extent requires a finite nonzero axis.');
        }
        const rotation = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis.normalize());
        return circularExtentPoints(Math.max(shape.innerRadius, shape.outerRadius), Math.max(shape.height, 1e-6) / 2)
            .map((point) => new THREE.Vector3(...point).applyQuaternion(rotation).toArray());
    }
    switch (spec.geometryKind) {
        case 'revolved-profile':
            return spec.profile.flatMap(([radius, height]) => circularExtentPoints(radius, 0).map(([x, , z]) => [x, height, z]));
        case 'toothed-band':
            return circularExtentPoints(toothedBandMaxRadius(spec), toothedBandMaxHalfHeight(spec));
        case 'torus':
            return circularExtentPoints(spec.ringRadius + spec.tubeRadius, spec.tubeRadius);
        case 'annular-band':
            return circularExtentPoints(spec.outerRadius, 0);
        case 'sector-slice':
        case 'sector-slice-outline':
            return circularExtentPoints(spec.outerRadius, sectorSliceHalfHeight(spec.innerRadius, spec.outerRadius, spec.outerThickness));
        case 'panel':
            if (spec.curvatureRadius !== undefined) {
                const halfAngle = spec.width / spec.curvatureRadius / 2;
                return boxExtentPoints([0, 0, spec.curvatureRadius * (Math.cos(halfAngle) - 1) / 2], [spec.curvatureRadius * Math.sin(halfAngle), spec.height / 2,
                    spec.curvatureRadius * (1 - Math.cos(halfAngle)) / 2]);
            }
            return boxExtentPoints([0, 0, 0], [spec.width / 2, spec.height / 2, 0]);
        case 'panel-outline':
            if (spec.curvatureRadius !== undefined) {
                const radius = spec.curvatureRadius + spec.z;
                const sag = radius * Math.cos(spec.width / spec.curvatureRadius / 2) - spec.curvatureRadius;
                return boxExtentPoints([0, 0, (sag + spec.z) / 2], [radius * Math.sin(spec.width / spec.curvatureRadius / 2), spec.height / 2, (spec.z - sag) / 2]);
            }
            return boxExtentPoints([0, 0, spec.z], [spec.width / 2, spec.height / 2, 0]);
        case 'sphere':
            return boxExtentPoints([0, 0, 0], [spec.radius, spec.radius, spec.radius]);
        case 'connector-tube':
            return boxExtentPoints([0, 0, 0], [0.5, 0.5, 0.5]);
        case 'cylinder-wall':
        case 'extruded-profile':
            throw new Error(`Shape required for ${spec.geometryKind} extent.`);
    }
}
